import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import managedAxios from "@/utils/managedAxios";
import { API_BASE } from "@/App";
import {
  BarChart3, ArrowLeft, TrendingUp, TrendingDown, Target,
  Loader2, Calendar, Award, Flame, CheckCircle, AlertTriangle,
  BookOpen, PenTool, Clock, Trophy, Zap
} from "lucide-react";

/**
 * TGPSC Analytics Page - Performance Dashboard
 * Reuses UPSC Analytics structure with TGPSC-specific metrics
 */

const TGPSCAnalyticsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);

  const isPro = user?.role === "PRO";

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(
        `${API_BASE}/analytics/dashboard?exam_type=TGPSC`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAnalyticsData(response.data);
    } catch (err) {
      console.error("Error fetching analytics:", err);
      // Demo data
      setAnalyticsData({
        overview: {
          selection_probability: 42.5,
          overall_accuracy: 68,
          total_questions: 245,
          total_answers: 12,
          streak: 5
        },
        subject_performance: [
          { subject: "Telangana History", accuracy: 75, questions: 45, trend: "up" },
          { subject: "Telangana Geography", accuracy: 62, questions: 38, trend: "up" },
          { subject: "Indian Polity", accuracy: 71, questions: 52, trend: "stable" },
          { subject: "Indian Economy", accuracy: 58, questions: 40, trend: "down" },
          { subject: "Current Affairs", accuracy: 65, questions: 35, trend: "up" },
          { subject: "Telangana Movement", accuracy: 80, questions: 35, trend: "up" },
        ],
        weak_areas: [
          { topic: "Nizam Era Administration", accuracy: 45, priority: "high" },
          { topic: "Fiscal Policy", accuracy: 48, priority: "high" },
          { topic: "Environmental Laws", accuracy: 52, priority: "medium" },
        ],
        weekly_progress: {
          questions_this_week: 78,
          questions_last_week: 65,
          accuracy_change: 3.5,
          streak_days: 5
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "up": return <TrendingUp className="w-4 h-4 text-emerald-500" />;
      case "down": return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <span className="w-4 h-4 text-slate-400">—</span>;
    }
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 70) return "text-emerald-600";
    if (accuracy >= 50) return "text-amber-600";
    return "text-red-600";
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "high": return <Badge className="bg-red-500 text-white">High Priority</Badge>;
      case "medium": return <Badge className="bg-amber-500 text-white">Medium</Badge>;
      default: return <Badge variant="outline">Low</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-indigo-50/30 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto text-purple-500 animate-spin mb-4" />
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const data = analyticsData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-indigo-50/30 pb-20">
      {/* Back Navigation */}
      <div className="max-w-5xl mx-auto px-4 pt-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/tgpsc")}
          className="text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          TGPSC Dashboard
        </Button>
      </div>

      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 pt-6 pb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <BarChart3 className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
              Performance Analytics
            </h1>
            <p className="text-slate-600">Track your TGPSC preparation progress</p>
          </div>
          <Badge className="bg-pink-500 ml-auto">TGPSC</Badge>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="max-w-5xl mx-auto px-4 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Selection Probability */}
          <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 mx-auto mb-2 opacity-80" />
              <div className="text-3xl font-bold">{data?.overview?.selection_probability || 0}%</div>
              <div className="text-xs text-purple-200">Selection Prob.</div>
            </CardContent>
          </Card>

          {/* Overall Accuracy */}
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
              <div className={`text-3xl font-bold ${getAccuracyColor(data?.overview?.overall_accuracy)}`}>
                {data?.overview?.overall_accuracy || 0}%
              </div>
              <div className="text-xs text-slate-600">Accuracy</div>
            </CardContent>
          </Card>

          {/* Total Questions */}
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <BookOpen className="w-6 h-6 mx-auto mb-2 text-blue-500" />
              <div className="text-3xl font-bold text-slate-800">
                {data?.overview?.total_questions || 0}
              </div>
              <div className="text-xs text-slate-600">MCQs Done</div>
            </CardContent>
          </Card>

          {/* Total Answers */}
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <PenTool className="w-6 h-6 mx-auto mb-2 text-purple-500" />
              <div className="text-3xl font-bold text-slate-800">
                {data?.overview?.total_answers || 0}
              </div>
              <div className="text-xs text-slate-600">Answers Written</div>
            </CardContent>
          </Card>

          {/* Streak */}
          <Card className="bg-gradient-to-br from-orange-500 to-amber-500 text-white">
            <CardContent className="p-4 text-center">
              <Flame className="w-6 h-6 mx-auto mb-2" />
              <div className="text-3xl font-bold">{data?.overview?.streak || 0}</div>
              <div className="text-xs text-orange-200">Day Streak</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Subject Performance */}
      <div className="max-w-5xl mx-auto px-4 mb-8">
        <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-purple-600" />
          Subject Performance
        </h2>
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {data?.subject_performance?.map((subject, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800">{subject.subject}</span>
                      {getTrendIcon(subject.trend)}
                    </div>
                    <div className="mt-2 flex items-center gap-4">
                      <Progress 
                        value={subject.accuracy} 
                        className="h-2 flex-1 max-w-xs"
                      />
                      <span className={`text-sm font-medium ${getAccuracyColor(subject.accuracy)}`}>
                        {subject.accuracy}%
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline">{subject.questions} Qs</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weak Areas */}
      <div className="max-w-5xl mx-auto px-4 mb-8">
        <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Weak Areas to Focus
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data?.weak_areas?.map((area, idx) => (
            <Card key={idx} className="border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-slate-800">{area.topic}</h3>
                  {getPriorityBadge(area.priority)}
                </div>
                <div className="flex items-center gap-2">
                  <Progress value={area.accuracy} className="h-2 flex-1" />
                  <span className={`text-sm font-medium ${getAccuracyColor(area.accuracy)}`}>
                    {area.accuracy}%
                  </span>
                </div>
                <Button
                  size="sm"
                  className="w-full mt-3 bg-pink-500 hover:bg-pink-600"
                  onClick={() => navigate(`/tgpsc/mcq?topic=${encodeURIComponent(area.topic)}`)}
                >
                  Practice Now
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Weekly Progress */}
      <div className="max-w-5xl mx-auto px-4">
        <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-500" />
          This Week's Progress
        </h2>
        <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold">{data?.weekly_progress?.questions_this_week || 0}</div>
                <div className="text-sm text-blue-200">Questions This Week</div>
                <div className="text-xs text-blue-300 mt-1">
                  vs {data?.weekly_progress?.questions_last_week || 0} last week
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold flex items-center justify-center gap-1">
                  {data?.weekly_progress?.accuracy_change > 0 ? "+" : ""}
                  {data?.weekly_progress?.accuracy_change || 0}%
                  {data?.weekly_progress?.accuracy_change > 0 ? (
                    <TrendingUp className="w-5 h-5" />
                  ) : data?.weekly_progress?.accuracy_change < 0 ? (
                    <TrendingDown className="w-5 h-5" />
                  ) : null}
                </div>
                <div className="text-sm text-blue-200">Accuracy Change</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">{data?.weekly_progress?.streak_days || 0}</div>
                <div className="text-sm text-blue-200">Day Streak</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">
                  {Math.round((data?.weekly_progress?.questions_this_week || 0) / 7)}
                </div>
                <div className="text-sm text-blue-200">Avg/Day</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TGPSCAnalyticsPage;
